import React, { Component, useState, useRef, useEffect } from 'react'
import {
  MediaPlayer,
  MediaProvider,
  Controls,
  PlayButton,
  MuteButton,
  VolumeSlider,
  Time,
  TimeSlider,
  useMediaState,
  usePlaybackRateOptions,
  formatTime,
} from '@vidstack/react'
import {
  MuteIcon,
  PauseIcon,
  PlayIcon,
  ReplayIcon,
  VolumeLowIcon,
  VolumeHighIcon,
  OdometerIcon,
} from '@vidstack/react/icons'
import { inject, observer } from 'mobx-react'

import './Audio.scss'

const AudioPlayerMuteButton = () => {
  const $muted = useMediaState('muted')
  const $volume = useMediaState('volume')
  return (
    <MuteButton className="vds-button">
      {$muted || $volume == 0 ? (
        <MuteIcon className="vds-icon" />
      ) : $volume < 0.5 ? (
        <VolumeLowIcon className="vds-icon" />
      ) : (
        <VolumeHighIcon className="vds-icon" />
      )}
    </MuteButton>
  )
}

const AudioPlayerVolumeSlider = () => {
  return (
    <VolumeSlider.Root className="vds-volume-slider vds-slider">
      <VolumeSlider.Track className="vds-slider-track" />
      <VolumeSlider.TrackFill className="vds-slider-track-fill vds-slider-track" />
      <VolumeSlider.Thumb className="vds-slider-thumb" />
    </VolumeSlider.Root>
  )
}

const AudioPlayerVolumePopup = () => {
  return (
    <div className="vds-volume">
      <AudioPlayerMuteButton />
      <div className="vds-volume-popup">
        <AudioPlayerVolumeSlider />
      </div>
    </div>
  )
}

const AudioPlayerPlayButton = () => {
  const $paused = useMediaState('paused')
  const $ended = useMediaState('ended')
  return (
    <PlayButton className="vds-button Audio__play-button">
      {$ended ? (
        <ReplayIcon className="vds-icon" />
      ) : $paused ? (
        <PlayIcon className="vds-icon" />
      ) : (
        <PauseIcon className="vds-icon" />
      )}
    </PlayButton>
  )
}

const AudioPlayerTimeSlider = ({ onTimeTextChange }) => {
  const $duration = useMediaState('duration')
  return (
    <TimeSlider.Root
      className="vds-time-slider vds-slider"
      onValueChange={(percent) => {
        onTimeTextChange(formatTime((percent / 100) * $duration))
      }}
    >
      <TimeSlider.Track className="vds-slider-track" />
      <TimeSlider.TrackFill className="vds-slider-track-fill vds-slider-track" />
      <TimeSlider.Thumb className="vds-slider-thumb" />
      <TimeSlider.Preview className="vds-slider-preview">
        <TimeSlider.Value className="vds-slider-value" />
      </TimeSlider.Preview>
    </TimeSlider.Root>
  )
}

const AudioPlayerTimeInfo = ({ timeText }) => {
  const $duration = useMediaState('duration')
  const $playbackRate = useMediaState('playbackRate')
  if (!$duration) return null
  return (
    <div className="vds-time-group">
      {timeText != null ? (
        <div className="vds-time">{timeText}</div>
      ) : (
        <Time className="vds-time" type="current" />
      )}
      <div className="vds-time-divider">/</div>
      <Time className="vds-time" type="duration" />
      <div className="vds-time-divider">/</div>
      <div className="vds-time Audio__speed-label">{$playbackRate}x</div>
    </div>
  )
}

const AudioPlayerSpeedMenu = () => {
  const options = usePlaybackRateOptions({
    rates: [0.5, 1, 1.25, 1.5, 2],
    normalLabel: null,
  })
  const [open, setOpen] = useState(false)
  const menuRef = useRef(null)

  useEffect(() => {
    if (!open) return
    const handler = (e) => {
      if (!menuRef.current?.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  return (
    <div className="vds-menu" ref={menuRef}>
      <button
        className="vds-button Audio__speed-button"
        disabled={options.disabled}
        onClick={() => setOpen((prev) => !prev)}
      >
        <OdometerIcon className="vds-icon" />
      </button>
      {open && (
        <div className="vds-menu-content">
          {options.map(({ label, value, select }) => (
            <div
              className={`vds-radio${
                value === options.selectedValue ? ' vds-radio--selected' : ''
              }`}
              key={value}
              onClick={() => {
                select()
                setOpen(false)
              }}
            >
              {label}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

const AudioPlayerLayout = () => {
  const $viewType = useMediaState('viewType')
  const [timeText, setTimeText] = useState(null)
  const isMatch = $viewType === 'audio'
  return (
    <div className="vds-audio-layout" data-match={isMatch ? '' : null}>
      <Controls.Root className="vds-controls">
        <Controls.Group className="vds-controls-group">
          <AudioPlayerPlayButton />
          <AudioPlayerVolumePopup />
          <AudioPlayerTimeSlider onTimeTextChange={setTimeText} />
          <AudioPlayerTimeInfo timeText={timeText} />
          <AudioPlayerSpeedMenu />
        </Controls.Group>
      </Controls.Root>
    </div>
  )
}

const AudioPlayer = ({ onEnd, onPlay, onPause, src }) => {
  if (!src || (Array.isArray(src) && !src.length)) return null
  return (
    <MediaPlayer onEnd={onEnd} onPlay={onPlay} onPause={onPause} src={src}>
      <MediaProvider />
      <AudioPlayerLayout />
    </MediaPlayer>
  )
}

@inject('uiStore')
@observer
class Audio extends Component {
  componentWillUnmount() {
    const { uiStore } = this.props
    uiStore.setAudioPause()
  }
  onEnd = () => {
    const { onEnd, uiStore } = this.props
    uiStore.setAudioPause()
    if (onEnd) onEnd()
  }
  onPlay = () => {
    const { onPlay, uiStore } = this.props
    uiStore.setAudioPlay()
    if (onPlay) onPlay()
  }
  onPause = () => {
    const { onPause, uiStore } = this.props
    uiStore.setAudioPause()
    if (onPause) onPause()
  }
  render() {
    const { src } = this.props
    if (!src || (Array.isArray(src) && !src.length)) return null
    return (
      <div className="Audio">
        <AudioPlayer
          onEnd={this.onEnd}
          onPlay={this.onPlay}
          onPause={this.onPause}
          src={src}
        />
      </div>
    )
  }
}

export default Audio
